/**
 * ChildProfileHeader — single shared header for every child page.
 *
 * Replaces the duplicated `.cp-header-card` blocks in Growth.jsx and
 * Vaccination.jsx, and replaces the inline-subtitle pattern in
 * ChildProfilePage.jsx. The header always presents the child the same
 * way regardless of which page is open:
 *
 *  - 64px Avatar inside a soft ring
 *  - Name (h2) + meta row of chips: gender badge, copyable Reg ID, DOB, age
 *  - Optional right-aligned action area (passed in by the page)
 *
 * The surface itself uses a soft mesh-gradient (var(--pt-gradient-hero))
 * so it visually matches the WelcomeHero on the home page.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiUser, FiCalendar, FiActivity, FiCheckSquare } from 'react-icons/fi';
import { MdOutlineVaccines } from 'react-icons/md';
import { Avatar, StatusBadge, RegIdChip } from '../../theme/components';
import { formatDateDisplay, calculateAge } from '../../utils/dates';
import './ChildProfileHeader.css';

const TABS = [
    { key: 'profile', label: 'Profile', to: (id) => `/profiles/${id}`, icon: FiUser },
    { key: 'vaccination', label: 'Vaccination', to: (id) => `/vaccination/${id}`, icon: MdOutlineVaccines },
    { key: 'growth', label: 'Growth', to: (id) => `/growth/${id}`, icon: FiActivity },
    { key: 'milestones', label: 'Milestones', to: (id) => `/milestones/${id}`, icon: FiCheckSquare },
];

const TabBar = ({ id, active }) => (
    <nav className="cph-tabs" aria-label="Child sections">
        {TABS.map((t) => {
            const Icon = t.icon;
            return (
                <Link
                    key={t.key}
                    to={t.to(id)}
                    className={`cph-tab ${active === t.key ? 'is-active' : ''}`}
                    aria-current={active === t.key ? 'page' : undefined}
                >
                    <Icon size={16} strokeWidth={2.25} />
                    <span>{t.label}</span>
                </Link>
            );
        })}
    </nav>
);

export function ChildProfileHeader({ profile, active, actions = null, showTabs = true }) {
    const isFemale = profile?.gender?.toLowerCase() === 'female';
    const age = useMemo(
        () => (profile?.dob ? calculateAge(profile.dob) : '—'),
        [profile?.dob],
    );

    if (!profile) return null;

    return (
        <section className={`cph-shell ${isFemale ? 'is-female' : ''}`}>
            <div className="cph-bg" aria-hidden="true" />
            <div className="cph-inner">
                <div className="cph-row">
                    <div className="cph-identity">
                        <div className="cph-avatar-ring">
                            <Avatar
                                name={profile.name}
                                id={profile.id || profile.childNo}
                                size={64}
                                sx={(theme) => ({
                                    bgcolor: isFemale ? '#ec4899' : theme.palette.primary.main,
                                })}
                            />
                            <span className="cph-avatar-pulse" aria-hidden="true" />
                        </div>
                        <div className="cph-text">
                            <h2 className="cph-name">{profile.name}</h2>
                            <div className="cph-meta">
                                <StatusBadge severity="neutral" size="small">
                                    {profile.gender?.toUpperCase()}
                                </StatusBadge>
                                <RegIdChip value={profile.childNo} size="small" />
                                <span className="cph-meta__chip">
                                    <FiCalendar size={12} aria-hidden="true" />
                                    <span>{formatDateDisplay(profile.dob) || '—'}</span>
                                </span>
                                <span className="cph-meta__chip cph-meta__chip--accent">
                                    <span className="cph-meta__dot" aria-hidden="true" />
                                    <span>{age || '—'}</span>
                                </span>
                            </div>
                            {profile.ownerName ? (
                                <div
                                    className="cph-owner"
                                    title={profile.ownerIdCode || ''}
                                >
                                    Added by {profile.ownerName}
                                    {profile.ownerIdCode
                                        ? ` (${profile.ownerIdCode})`
                                        : ''}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {actions ? <div className="cph-actions">{actions}</div> : null}
                </div>
                {showTabs ? <TabBar id={profile.id} active={active} /> : null}
            </div>
        </section>
    );
}

export default ChildProfileHeader;
